/* eslint-disable max-lines */
import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { useSize } from '@fuxi/eevee-hooks';
import { ModelViewerRef, Snapshot, Workflow } from '@fuxi/model-viewer-react';
import { useQuery } from '@tanstack/react-query';
import { isArray, isEqual, isNil } from 'lodash-es';
import { EditorMode } from '@fuxi/react-img-editor';
import { getPreviewTypeAndUrl } from 'common/utils/versionDetail';
import {
  ExportVersionDetail,
  TaskDetailPageParams,
  ExportFileType,
  FileCheckResult,
  TaskMode,
  ExportVersionFile,
  CheckItem,
  FileCheckRuleItem,
} from 'common/types';
import { Annotation, CommentAssetPosition, Comment, AspectType } from '@/types';
import service from '@/service';
import { useAppDispatch, useAppSelector, useCachedComment } from '@/hooks';
import { PlayViewerControlRef } from '@/components/PlayViewerControl';

import { dataURItoBlob, blobToFile } from '@/utils';
import {
  selectPreviewMode,
  setCurCommentId,
  setIsCommentDrawerOpen,
  setIsInspecting as setIsInspectingAction,
  setNeedsRebuildAnnotation,
  setPreviewMode,
} from '@/store/comment';
import { compareVersion } from '@/utils/version';
import { VideoTransform } from '@/types/playControl';
import {
  selectFileList,
  setFileList as dispatchSetFileList,
  setCurVersion as dispatchSetCurVersion,
} from '@/store/version';
import { CommentToolRef } from '@/components/CommentToolLight';
import { FilterCheckItemMap } from 'common/constants';

const METALLIC_ROUGHNESS_AO = 'metallic-roughness-ao';
const SPECULAR_GLOSSINESS = 'specular-glossiness';

interface Props {
  projectConfig?: any;
}

export const useTaskEditor = ({ projectConfig: _projectConfig }: Props) => {
  const dispatch = useAppDispatch();
  const fileList = useAppSelector(selectFileList);
  const previewMode = useAppSelector(selectPreviewMode);

  const params = useParams() as TaskDetailPageParams; //任务id
  const id = Number(params.taskId);
  const versionId = Number(params.versionId);
  const { getCachedComment, syncCommentInfo, clearCachedComment } = useCachedComment(`eeveeCachedComment-${id}`);
  const [curVersion, setCurVersion] = useState<ExportVersionDetail>();
  const [isFullScreen, setIsFullScreen] = useState(false);
  //预览模式：WebGL,TurnTable,Image, NUll，进入批注后是NUll模式
  const [previewType, setPreviewType] = useState(ExportFileType.Null);
  //进入批注前，需要更改previewType释放webgl资源，cachePreviewType用于记录进入图片批注模式之前的状态
  const [cachedPreviewType, setCachedPreviewType] = useState(ExportFileType.Image);
  // const [curTurnPageIndex, setCurTurnPageIndex] = useState(0);
  const [fileLoading, setFileLoading] = useState(true);
  const [exportFile, setExportFile] = useState<string | string[] | null>();
  const [currentFileLoaded, setCurrentFileLoaded] = useState(false);
  //当前预览的文件索引
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentFile, setCurrentFile] = useState<ExportVersionFile>();
  const [isExportFileLoaded, setIsExportFileLoaded] = useState(false);
  //3D预览小窗显示
  const [modelViewerHide, setModelViewerHide] = useState(false);
  //3D预览出错
  const [isModelViewerError, setIsModelViewerError] = useState(false);

  //禁止轮询
  const [disableQuery, setDisableQuery] = useState(false);

  const [editorMode, setEditorMode] = useState(EditorMode.ANNOTATION);

  //缓存视频时间
  const [cachedPlayerInfo, setCachedPlayerInfo] = useState<{
    frameIndex: number;
    frameRate?: number;
    takeIndex?: number;
  }>({
    frameIndex: 0,
    frameRate: 0,
  });

  //模型检查器是否显示
  const [modelInspectShow, setModelInspectShow] = useState(true);
  // 预览失败
  const [isPreviewError, setIsPreviewError] = useState(false);

  //主图截图
  const snapShotRef = useRef<Record<string, Snapshot | undefined>>({});
  const [miniSnapShot, setMiniSnapShot] = useState<File | null>(null);
  const modelViewerRef = useRef<ModelViewerRef>(null);
  // const turnTableRef = useRef<number>(curTurnPageIndex);
  const renderImageRef = useRef<any>();
  const renderImageSize = useSize(renderImageRef);
  const playerCtrRef = useRef<PlayViewerControlRef | undefined>();
  const commentToolRef = useRef<CommentToolRef | undefined>();

  //设置workflow
  const [workflow, setWorkflow] = useState<Workflow>();
  const [projectConfig, setProjectConfig] = useState(
    _projectConfig || {
      scale: 1,
      refModels: [],
    },
  );
  //设置批注图形内容
  const [annotation, setAnnotation] = useState<Annotation>({
    mainImage: '',
    stage: undefined,
    images: {},
    pluginName: 'pen',
  });

  //视频的transform
  const [playerTransform, setPlayerTransform] = useState<VideoTransform>({
    scale: 1,
    transX: 0,
    transY: 0,
    width: 0,
    height: 0,
  });

  // 是否还原注释的内容
  const [isAnnotationShow, setIsAnnotationShow] = useState(false);
  const [isCommentToolActive, setIsCommentToolActive] = useState(false);
  // const [fileList, setFileList] = useState<ExportVersionFile[]>([]);
  const [currentComment, setCurrentComment] = useState<Comment>();
  //播放器是否在播放
  const [isPlayerPause, setIsPlayerPause] = useState(true);
  //播放器是否是列表播放
  const [isPlayList, setIsPlayList] = useState(false);
  const [playList, setPlayList] = useState<ExportVersionFile[]>([]);
  const [currentPlayIndex, setCurrentPlayIndex] = useState(0);

  // 事件监听访问
  const videoStyleRef = useRef({ ...playerTransform });
  const [aspectMode, setAspectMode] = useState(AspectType.aspectFit);

  const { data: currentVersion } = useQuery(
    ['getTaskCurrentVersionFile', curVersion?.uniqueId],
    () => service.exportVersions.getTaskCurrentVersionFile(curVersion?.uniqueId!),
    { enabled: !!curVersion?.uniqueId && !disableQuery },
  );

  const setWorkflowByString = (workflowString: string) => {
    if (workflowString?.includes('specular')) {
      setWorkflow(Workflow.SG);
    } else if (workflowString === 'custom-l10') {
      setWorkflow(Workflow.L10);
    } else if (workflowString === 'custom-l36') {
      setWorkflow(Workflow.L36);
    } else {
      setWorkflow(Workflow.MRA);
    }
  };
  const sortRulesByType = (rules: FileCheckRuleItem[]) => {
    if (!rules || rules.length === 0) return [];
    const newRules = [...rules];
    //rules根据ruleType在FilterCheckItemMap里的数值进行排序
    return newRules.sort((a, b) => {
      const aValue = FilterCheckItemMap[a.ruleType] || 17;
      const bValue = FilterCheckItemMap[b.ruleType] || 17;
      return aValue - bValue;
    });
  };

  const setCurVersionWrap = useCallback((data: ExportVersionDetail) => {
    let fileList =
      data.fileList?.map((file) => {
        const rules = !file || !file.info ? [] : Array.isArray(file.info) ? file.info : [file.info];

        return {
          ...file,
          uuid: file.fileId, // 兼容旧数据
          rules: sortRulesByType(rules),
          // exportFileUrl:
          //   'https://cdn.fuxi.netease.com/fuxi-art-platform/theme-dark-light/portal/steps/show01.png?auth_key=2147483647-0-0-fffc1cb9d32960f68bdc3eddbc6ce05b',
        };
      }) || [];

    if (!data.qualityInspection || Object.keys(data.qualityInspection).length === 0) {
      setCurVersion({ ...data, fileList, isCheckPass: false });
      return fileList;
    }
    const checkList = isArray(data?.qualityInspection)
      ? data?.qualityInspection?.reduce((ac, cur) => [...ac, ...cur?.checkList], [] as any)
      : data?.qualityInspection?.checkList || [];
    const isCheckPass = checkList?.every?.(({ status }) => status === FileCheckResult.Pass);
    // 0.7.5 改造，部分元信息需要从fileList的rules取
    // 需要优先遍历checkList，再合并fileName相同值的条目到checkList
    let mergedCheckList = [];
    for (let i = 0; i < fileList.length; i++) {
      const item = fileList[i];
      if (item.fileName) {
        // 检查checkList里是否存在item
        const existingItem = checkList.find((file) => item.fileName === file.fileName);
        if (existingItem) {
          //合并rules
          const rules = [...(existingItem.rules || []), ...(item.rules || [])];
          // rules根据ruleType去重
          const uniqueRules = Array.from(new Map(rules.map((rule) => [rule.ruleType, rule])).values());
          const newItem = {
            ...existingItem,
            rules: sortRulesByType(uniqueRules),
            uuid: item.uuid, // 兼容旧数据
            fileId: item.fileId,
          };
          mergedCheckList.push(newItem);
        } else {
          mergedCheckList.push(item);
        }
      }
    }
    setCurVersion({ ...data, fileList: mergedCheckList, isCheckPass });
    return mergedCheckList;
  }, []);

  const setCurTempVersion = useCallback((data) => {
    const checkList = data?.checkList;
    const isCheckPass = data?.status === FileCheckResult.Pass;
    setCurVersion({ ...data, isCheckPass });
    return checkList;
  }, []);

  //画板模式,入口下掉，暂时废弃
  // eslint-disable-next-line react-func/max-lines-per-function
  const onPaletteEnable = async () => {
    setFileLoading(true);
    setCachedPreviewType(previewType);
    setIsAnnotationShow(false);
    const file = fileList[currentIndex];
    const indexKey = 'uuid';
    const fileId = file[indexKey];

    try {
      let str;
      const isBabylonError = (!exportFile || isModelViewerError) && previewType === ExportFileType.Babylon;
      if (isBabylonError) {
        str = 'https://mmvp-fos.apps-hp.danlu.netease.com/api/sdk/0/version_file$$$0$$$1687957623/error_default.png';
      } else if (previewType === ExportFileType.Other) {
        str = '';
      } else if (previewType === ExportFileType.Babylon) {
        // 如果当前是3D预览模式，需要先3D场景截图
        const snap = (await playerCtrRef.current?.takeSnapshot()) as Snapshot;
        if (exportFile && snapShotRef.current) {
          snapShotRef.current[fileId] = snap;
        }

        const blob = dataURItoBlob(snap.image as string);
        const newImageFile = blobToFile(blob, `${uuidv4()}.png`, blob.type);
        str = await service.upload.uploadFileByProxy(newImageFile, 'TASK_MATERIAL_LIBRARY', false);
      } else if (previewType === ExportFileType.TurnTable) {
        if (exportFile && isArray(exportFile)) {
          str = exportFile[0];
        }
      } else if (previewType === ExportFileType.Video) {
        //暂停
        playerCtrRef.current?.pause();
        setIsPlayerPause(true);

        setCachedPlayerInfo({
          frameIndex: playerCtrRef.current?.getCurrentFrameIndex() || 1,
          frameRate: playerCtrRef.current?.getCurrentFrameRate() || 0,
          takeIndex: playerCtrRef.current?.getCurrentTakeIndex() || 0,
        });
        str = '';
      } else if (previewType === ExportFileType.Animation) {
        playerCtrRef.current?.pause();
        str = '';
      } else {
        str = exportFile as string;
      }
      setAnnotation({ mainImage: str, stage: undefined, images: {}, pluginName: 'pen' });
      dispatch(setPreviewMode(TaskMode.Edit));
      setEditorMode(EditorMode.PALETTE);

      if (previewType === ExportFileType.Video || previewType === ExportFileType.Animation) {
        setIsAnnotationShow(true);
      } else {
        setIsAnnotationShow(false);
      }

      setFileLoading(false);
      setDisableQuery(true);
      setMode(TaskMode.Edit);
    } catch (e) {
      console.error(e);
    }
  };

  // 清空注释
  const onPlayIndexChange = () => {
    dispatch(setCurCommentId(-1));
    dispatch(setNeedsRebuildAnnotation(false));
    dispatch(setIsCommentDrawerOpen(false));
    // setIsCommentToolActive(false);

    commentToolRef.current?.clearAnnotation();
  };

  //预览文件切换
  const onPreviewFileChange = async (list, index) => {
    console.log('onPreviewFileChange', list, index);
    if (!list || list.length === 0 || index < 0) {
      return;
    }
    const file = list[index];
    // setAspectMode(AspectType.aspectFit);

    // //为了保存webGL预览的角度相关数据
    // if (previewType === ExportFileType.Babylon && !isModelViewerError) {
    //   const snap = (await playerCtrRef.current?.takeSnapshot()) as Snapshot;
    //   if (snapShotRef.current && exportFile) {
    //     snapShotRef.current[file.fileId] = snap;
    //   }
    // }
    // if (previewType === ExportFileType.TurnTable) {
    //   setCurTurnPageIndex(turnTableRef.current);
    // }
    //判断当前文件是什么类型，进入什么预览模式，获取当前的预览url
    const { type, url } = getPreviewTypeAndUrl(file);
    if (!isEqual(url, exportFile)) {
      if (exportFile) {
        await saveDefaultComment();
      }

      dispatch(setCurCommentId(-1));
      dispatch(setNeedsRebuildAnnotation(false));
      dispatch(setIsCommentDrawerOpen(false));
      // setIsCommentToolActive(false);
      setFileLoading(true);
      setIsPreviewError(false);
      setCurrentFileLoaded(false);
      commentToolRef.current?.clearAnnotation();
      setPlayerTransform({ scale: 1, transX: 0, transY: 0, width: 0, height: 0 });
      videoStyleRef.current = { scale: 1, transX: 0, transY: 0, width: 0, height: 0 };

      if (type === ExportFileType.Other) {
        // dispatch(setIsInspectingAction(true));
        setFileLoading(false);
        setIsPreviewError(true);
      } else if (
        !(type === ExportFileType.Babylon || type === ExportFileType.TurnTable || type === ExportFileType.Animation)
      ) {
        // dispatch(setIsInspectingAction(false));
      }
      setCachedPreviewType(type);
      setExportFile(url);
      setPreviewType(type);
      setCurrentIndex(index);
      // setCurrentFile(file);
    } else {
      console.log('onPreviewFileChange1', url, exportFile);
      setCurrentIndex(index);
    }
  };

  useEffect(() => {
    setCurrentFile(fileList[currentIndex]);
  }, [currentIndex, fileList]);

  const onModelViewerSnapShot = async () => {
    const snapshot = (await playerCtrRef.current?.takeSnapshot()) as Snapshot;
    const blob = dataURItoBlob(snapshot.image as string);
    const newImageFile = blobToFile(blob, `${uuidv4()}.png`, blob.type);
    setMiniSnapShot(newImageFile);
  };

  //恢复缓存的反馈
  const rebuildCachedComment = async () => {
    const cachedComment = getCachedComment();
    setFileLoading(true);
    setCachedPreviewType(previewType);
    try {
      setPreviewType(ExportFileType.Null);
      if (cachedComment?.annotation) {
        setAnnotation(cachedComment?.annotation);
      }
      setMode(TaskMode.Edit);
      setEditorMode(EditorMode.PALETTE);
      // dispatch(setPreviewMode(TaskMode.Edit));
      setFileLoading(false);
      setDisableQuery(true);
    } catch (e) {
      console.error(e);
    }
  };

  const takePlayerCtrlSnapShot = async (fileId: string) => {
    const snap = await playerCtrRef.current?.takeSnapshot();

    snapShotRef.current[fileId] = snap;

    return snap;
  };

  const restorePlayer = async (assetPosition: CommentAssetPosition) => {
    if (playerCtrRef.current) {
      if (playerCtrRef.current.changeTakeIndex) {
        //判断是否需要切换动画takeIndex
        playerCtrRef.current.changeTakeIndex(assetPosition.takeIndex);
      }
      const snapShot = assetPosition.snapShot;
      console.log('restorePlayer!!!', snapShot);
      playerCtrRef.current?.restoreSnapshot(snapShot);
    }
  };

  const setFileList = (files: ExportVersionFile[]) => {
    dispatch(dispatchSetFileList(files));
  };
  const setMode = (mode: TaskMode) => {
    dispatch(setPreviewMode(mode));
  };

  // 0.7.5质检改造废弃，因为dccVersion在质检结果里，质检中是没有的
  const is030DCCVersion = useCallback(() => {
    //判断curVersion?.qualityInspection?.dccVersion大于等于0.3.0版本
    return compareVersion(curVersion?.qualityInspection?.dccVersion ?? '0.0.0', '0.3.0');
  }, [curVersion]);

  const findIndexInFileList = (fileId: string, list: Array<ExportVersionFile>, is030Version: boolean) => {
    // 0.3.0后，用uuid作为唯一的索引
    const compareKey = is030Version ? 'uuid' : 'id';

    let index = -1;
    list.forEach((item, i) => {
      if (item[compareKey] === fileId) {
        index = i;
      }
    });
    return index;
  };

  // 根据comment里记录的fileId查找对应的文件
  const getFileByComment = (comment: Comment) => {
    const fileIndex = findIndexInFileList(comment.assetPosition?.fileId, fileList, true);
    if (fileIndex > -1) {
      // let { url, type } = getPreviewTypeAndUrl(fileList[fileIndex]);
      // //比较preview.url与exportFile是否相同，不相同则需要重新加载
      // console.log('fileIndex url ,exportFile!!!', fileIndex, url, exportFile);
      // if (!isEqual(url, exportFile)) {
      //   onPreviewFileChange(fileList, fileIndex);
      // }
      return {
        file: fileList[fileIndex],
        index: fileIndex,
        fileId: comment.assetPosition?.fileId,
      };
    }
    return {
      file: null,
      index: -1,
    };
  };
  const saveDefaultComment = (mustSaveStage?: boolean) => {
    if (commentToolRef.current) {
      return commentToolRef.current.saveAnnotation({
        isChecked: true,
        content: '',
        attachment: [],
        mustSaveStage,
      });
    } else {
      return Promise.resolve();
    }
  };

  const fileIdByCurrentIndex = useMemo(() => {
    if (isNil(currentIndex) || currentIndex === -1) {
      return null;
    }
    // const indexFileKey = curVersion?.qualityInspection?.dccVersion === '0.3.0' ? 'uuid' : 'id';
    const indexFileKey = 'uuid';
    if (fileList[currentIndex]) {
      return fileList[currentIndex][indexFileKey];
    }
    return null;
  }, [currentIndex, fileList, curVersion]);

  useEffect(() => {
    currentVersion && setCurVersionWrap(currentVersion);
    dispatch(dispatchSetCurVersion(currentVersion));
  }, [currentVersion]);

  return {
    id,
    versionId,
    snapShotRef,
    modelViewerRef,
    // turnTableRef,
    mode: previewMode,
    setMode,
    curVersion,
    setCurVersion: setCurVersionWrap,
    setCurTempVersion,
    exportFile,
    setExportFile,
    previewType,
    setPreviewType,
    isFullScreen,
    setIsFullScreen,
    fileLoading,
    setFileLoading,
    cachedPreviewType,
    setCachedPreviewType,
    workflow,
    setWorkflow,
    projectConfig,
    setProjectConfig,
    setWorkflowByString,
    onPaletteEnable,
    rebuildCachedComment,
    annotation,
    setAnnotation,
    getCachedComment,
    syncCommentInfo,
    clearCachedComment,
    renderImageRef,
    renderImageSize,
    modelViewerHide,
    setModelViewerHide,
    onModelViewerSnapShot,
    miniSnapShot,
    setMiniSnapShot,
    disableQuery,
    setDisableQuery,
    isModelViewerError,
    setIsModelViewerError,
    currentIndex,
    setCurrentIndex,
    playerCtrRef,
    cachedPlayerInfo,
    setCachedPlayerInfo,
    playerTransform,
    setPlayerTransform,
    isAnnotationShow,
    setIsAnnotationShow,
    takePlayerCtrlSnapShot,
    isPlayerPause,
    setIsPlayerPause,
    isPlayList,
    setIsPlayList,
    fileList,
    setFileList,
    currentComment,
    setCurrentComment,
    onPreviewFileChange,
    restorePlayer,
    modelInspectShow,
    setModelInspectShow,
    editorMode,
    setEditorMode,
    commentToolRef,
    getFileByComment,
    saveDefaultComment,
    fileIdByCurrentIndex,
    isExportFileLoaded,
    setIsExportFileLoaded,
    currentFileLoaded,
    setCurrentFileLoaded,
    videoStyleRef,
    aspectMode,
    setAspectMode,
    isCommentToolActive,
    setIsCommentToolActive,
    currentPlayIndex,
    setCurrentPlayIndex,
    onPlayIndexChange,
    currentFile,
    setCurrentFile,
    playList,
    setPlayList,
    isPreviewError,
    setIsPreviewError,
  };
};
